import { useEffect, useMemo, useRef, useState } from 'react';
import { Gamepad2, Timer, SkipForward, Check, Play } from 'lucide-react';

// Alias — turn-based word explanation game.
// Individual lesson flow:
//  - Learner explains first (60s), then teacher (60s), then alternate.
//  - Only the current explainer sees the word.
//  - Both see the timer; explainer taps Skip / Guessed.
//  - Skipped words are discarded. Word pool is shared across rounds: each new round continues where the previous left off.
//  - Between rounds: review screen with guessed + skipped lists. Teacher can end the game there.
//  - Final: mirrored stats ("you guessed X, you explained Y").
//
// This component is a controlled game surface: it renders based on `gameState`
// and reports every state transition through `onGameStateChange`. In
// preview mode it wraps itself in local state so it plays on a single device
// without any role split.

const TURN_DURATION_SEC = 60;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const createInitialGameState = () => ({
  phase: 'idle',
  words: [],
  index: 0,
  rounds: [], // completed rounds: [{ explainer: 'student'|'teacher', guessed: [], skipped: [] }, ...]
  currentRound: null, // active/grace round: { explainer, guessed, skipped }
  roundStartedAt: null,
});

const startGame = (state, words) => ({
  ...state,
  phase: 'round_active',
  words: shuffle(words),
  index: 0,
  rounds: [],
  currentRound: { explainer: 'student', guessed: [], skipped: [] },
  roundStartedAt: Date.now(),
});

const isActivePhase = (state) => state.phase === 'round_active' || state.phase === 'round_grace';
const isGracePhase = (state) => state.phase === 'round_grace';

const nextExplainerFor = (state) => {
  const last = state.rounds[state.rounds.length - 1];
  return last?.explainer === 'student' ? 'teacher' : 'student';
};

const advanceWord = (state, kind) => {
  if (!state.currentRound) return state;
  const word = state.words[state.index];
  if (word == null) return state;
  const bucket = kind === 'guessed' ? 'guessed' : 'skipped';
  const updatedRound = {
    ...state.currentRound,
    [bucket]: [...state.currentRound[bucket], word],
  };
  const nextIndex = state.index + 1;
  const outOfWords = nextIndex >= state.words.length;
  const grace = isGracePhase(state);
  const shouldEnd = grace || outOfWords;
  if (shouldEnd) {
    return {
      ...state,
      index: nextIndex,
      rounds: [...state.rounds, updatedRound],
      currentRound: null,
      phase: 'round_review',
      roundStartedAt: null,
    };
  }
  return {
    ...state,
    currentRound: updatedRound,
    index: nextIndex,
  };
};

const endTurnByTimer = (state) => {
  if (state.phase !== 'round_active') return state;
  const hasWord = state.index < state.words.length;
  if (!hasWord) {
    return {
      ...state,
      rounds: state.currentRound ? [...state.rounds, state.currentRound] : state.rounds,
      currentRound: null,
      phase: 'round_review',
      roundStartedAt: null,
    };
  }
  return { ...state, phase: 'round_grace', roundStartedAt: null };
};

const startNextRound = (state) => ({
  ...state,
  phase: 'round_active',
  currentRound: { explainer: nextExplainerFor(state), guessed: [], skipped: [] },
  roundStartedAt: Date.now(),
});

const showFinal = (state) => ({ ...state, phase: 'final', currentRound: null, roundStartedAt: null });

// -------- Timer hook --------

const useTimeLeft = (state) => {
  const [now, setNow] = useState(() => Date.now());
  const isActive = state.phase === 'round_active';
  useEffect(() => {
    if (!isActive) return undefined;
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, [isActive]);
  if (!isActive || !state.roundStartedAt) return TURN_DURATION_SEC;
  return Math.max(0, TURN_DURATION_SEC - Math.floor((now - state.roundStartedAt) / 1000));
};

// -------- Static preview (editor canvas) --------

const StaticPreview = ({ words }) => (
  <div className="w-full h-full flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-brand-primary/40 bg-brand-sage/10 p-4 text-brand-primary">
    <Gamepad2 size={40} className="mb-2 opacity-70" />
    <div className="text-lg font-semibold">Alias</div>
    <div className="text-xs opacity-70 mt-1">
      {words.length > 0 ? `${words.length} word${words.length === 1 ? '' : 's'}` : 'No words yet'}
    </div>
  </div>
);

// -------- UI atoms --------

const Timer60 = ({ seconds }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-primary text-white text-sm font-semibold">
    <Timer size={14} />
    <span>{String(seconds).padStart(2, '0')}s</span>
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, icon: Icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2A4D25] text-white text-sm font-semibold hover:bg-[#1e3a1a] transition disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {Icon && <Icon size={16} />}
    <span>{children}</span>
  </button>
);

const ExplainerCard = ({ word, timeLeft, onGuessed, onSkip, wordsLeft }) => (
  <div className="w-full h-full flex flex-col items-center justify-between p-4">
    <div className="w-full flex items-center justify-between text-xs text-gray-600">
      <span>Words left: {wordsLeft}</span>
      {timeLeft != null ? <Timer60 seconds={timeLeft} /> : <span />}
    </div>
    <div className="flex-1 flex items-center justify-center w-full">
      <div className="text-4xl md:text-5xl font-bold text-gray-900 text-center break-words px-4">
        {word || '—'}
      </div>
    </div>
    <div className="flex gap-3 w-full justify-center">
      <button
        onClick={onSkip}
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition"
      >
        <SkipForward size={16} /> Skip
      </button>
      <button
        onClick={onGuessed}
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#2A4D25] text-white text-sm font-semibold hover:bg-[#1e3a1a] transition"
      >
        <Check size={16} /> Guessed
      </button>
    </div>
  </div>
);

const GuesserView = ({ timeLeft, score, wordsLeft, waitingLabel }) => (
  <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-4">
    {timeLeft != null && <Timer60 seconds={timeLeft} />}
    <div className="text-sm text-gray-500">{waitingLabel}</div>
    <div className="grid grid-cols-2 gap-6 text-center">
      <div>
        <div className="text-3xl font-bold text-brand-primary">{score}</div>
        <div className="text-xs text-gray-500">Guessed</div>
      </div>
      <div>
        <div className="text-3xl font-bold text-gray-700">{wordsLeft}</div>
        <div className="text-xs text-gray-500">Words left</div>
      </div>
    </div>
  </div>
);

const WordList = ({ title, words, tone }) => (
  <div className="min-w-0">
    <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${tone === 'good' ? 'text-emerald-700' : 'text-gray-500'}`}>
      {title} ({words.length})
    </div>
    <div className="flex flex-wrap gap-1.5">
      {words.length === 0 ? (
        <span className="text-xs text-gray-400 italic">none</span>
      ) : (
        words.map((w, i) => (
          <span
            key={`${w}-${i}`}
            className={`px-2 py-0.5 rounded text-xs ${
              tone === 'good' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600 line-through'
            }`}
          >
            {w}
          </span>
        ))
      )}
    </div>
  </div>
);

const ReviewScreen = ({ roundStats, ctaLabel, onCta, waitLabel, onEndGame }) => (
  <div className="w-full h-full flex flex-col p-4 gap-3 overflow-hidden">
    <div className="text-center">
      <div className="text-2xl font-bold text-brand-primary">
        {roundStats.guessed.length} guessed
      </div>
    </div>
    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
      <WordList title="Guessed" words={roundStats.guessed} tone="good" />
      <WordList title="Skipped" words={roundStats.skipped} tone="bad" />
    </div>
    <div className="flex flex-wrap justify-center gap-2 pt-2">
      {onCta ? (
        <PrimaryButton onClick={onCta} icon={Play}>{ctaLabel}</PrimaryButton>
      ) : waitLabel ? (
        <div className="text-sm text-gray-500 italic">{waitLabel}</div>
      ) : null}
      {onEndGame && (
        <button
          onClick={onEndGame}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition"
        >
          Завершити гру
        </button>
      )}
    </div>
  </div>
);

const sumByExplainer = (rounds, explainer, bucket) =>
  rounds
    .filter((r) => r.explainer === explainer)
    .reduce((acc, r) => acc + r[bucket].length, 0);

const FinalScreen = ({ state, userRole, previewMode }) => {
  const rounds = state.rounds;
  const studentExplained = sumByExplainer(rounds, 'student', 'guessed');
  const teacherExplained = sumByExplainer(rounds, 'teacher', 'guessed');

  const roundList = (
    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
      {rounds.map((r, i) => (
        <div key={i} className="space-y-1">
          <div className="text-xs font-semibold text-gray-500 uppercase pt-1">
            Round {i + 1} — {r.explainer === 'student' ? 'learner' : 'teacher'} explained
          </div>
          <WordList title="Guessed" words={r.guessed} tone="good" />
          <WordList title="Skipped" words={r.skipped} tone="bad" />
        </div>
      ))}
    </div>
  );

  if (previewMode || !userRole) {
    return (
      <div className="w-full h-full flex flex-col p-4 gap-3 overflow-hidden">
        <div className="text-center text-lg font-bold text-brand-primary">Game over</div>
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-brand-primary">{studentExplained}</div>
            <div className="text-xs text-gray-500">Learner explained</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-brand-primary">{teacherExplained}</div>
            <div className="text-xs text-gray-500">Teacher explained</div>
          </div>
        </div>
        {roundList}
      </div>
    );
  }
  const youGuessed = userRole === 'student' ? teacherExplained : studentExplained;
  const youExplained = userRole === 'student' ? studentExplained : teacherExplained;
  return (
    <div className="w-full h-full flex flex-col p-4 gap-3 overflow-hidden">
      <div className="text-center text-lg font-bold text-brand-primary">Game over</div>
      <div className="grid grid-cols-2 gap-4 text-center">
        <div>
          <div className="text-3xl font-bold text-brand-primary">{youGuessed}</div>
          <div className="text-xs text-gray-500">You guessed</div>
        </div>
        <div>
          <div className="text-3xl font-bold text-brand-primary">{youExplained}</div>
          <div className="text-xs text-gray-500">You explained</div>
        </div>
      </div>
      {roundList}
    </div>
  );
};

// -------- Core game surface (stateless w.r.t. game data) --------

const AliasGameCore = ({ words, state, onChange, userRole, previewMode }) => {
  const timeLeft = useTimeLeft(state);
  const endedRef = useRef(false);

  // Auto-end turn when timer hits 0.
  useEffect(() => {
    const isActive = state.phase === 'round_active';
    if (!isActive) {
      endedRef.current = false;
      return;
    }
    if (timeLeft === 0 && !endedRef.current) {
      endedRef.current = true;
      onChange(endTurnByTimer(state));
    }
  }, [timeLeft, state, onChange]);

  const currentExplainer = state.currentRound?.explainer ?? null;
  const isExplainer =
    previewMode ||
    (currentExplainer && userRole === currentExplainer);

  const canStartGame = previewMode || userRole === 'student';
  const nextExplainer = state.rounds.length > 0 ? nextExplainerFor(state) : 'student';
  const canStartNextRound = previewMode || userRole === nextExplainer;
  const canEndGame = previewMode || userRole === 'teacher';
  const wordsRemaining = state.index < state.words.length;

  const wordsLeftCount = Math.max(0, state.words.length - state.index);
  const currentWord = state.words[state.index];

  if (state.phase === 'idle') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-3 text-center">
        <Gamepad2 size={32} className="text-brand-primary" />
        <div className="text-lg font-semibold text-gray-800">Alias</div>
        <div className="text-sm text-gray-500 max-w-xs">
          Learner explains first. Teacher guesses. Then swap. One minute each round.
        </div>
        {canStartGame ? (
          <PrimaryButton
            onClick={() => onChange(startGame(state, words))}
            disabled={words.length === 0}
            icon={Play}
          >
            Start
          </PrimaryButton>
        ) : (
          <div className="text-xs text-gray-400 italic">Waiting for learner to start…</div>
        )}
      </div>
    );
  }

  if (isActivePhase(state)) {
    const score = state.currentRound?.guessed.length ?? 0;
    const grace = isGracePhase(state);
    const displayTimeLeft = grace ? null : timeLeft;
    if (isExplainer) {
      return (
        <ExplainerCard
          word={currentWord}
          timeLeft={displayTimeLeft}
          onGuessed={() => onChange(advanceWord(state, 'guessed'))}
          onSkip={() => onChange(advanceWord(state, 'skipped'))}
          wordsLeft={wordsLeftCount}
        />
      );
    }
    const explainerLabel = currentExplainer === 'student' ? 'Learner' : 'Teacher';
    return (
      <GuesserView
        timeLeft={displayTimeLeft}
        score={score}
        wordsLeft={wordsLeftCount}
        waitingLabel={grace ? `${explainerLabel} is finishing the last word…` : `${explainerLabel} is explaining`}
      />
    );
  }

  if (state.phase === 'round_review') {
    const lastRound = state.rounds[state.rounds.length - 1];
    if (!lastRound) return null;
    const canContinue = wordsRemaining;
    const nextLabel = nextExplainer === 'student' ? 'Start learner round' : 'Start teacher round';
    return (
      <ReviewScreen
        roundStats={lastRound}
        ctaLabel={nextLabel}
        onCta={canContinue && canStartNextRound ? () => onChange(startNextRound(state)) : null}
        waitLabel={
          canContinue
            ? nextExplainer === 'student'
              ? 'Waiting for learner…'
              : 'Waiting for teacher…'
            : ''
        }
        onEndGame={canEndGame ? () => onChange(showFinal(state)) : null}
      />
    );
  }

  return <FinalScreen state={state} userRole={userRole} previewMode={previewMode} />;
};

// -------- Public entry point --------

const AliasElement = ({
  element,
  isEditor = false,
  previewMode = false,
  userRole = null,
  gameState = null,
  onGameStateChange = null,
}) => {
  const words = useMemo(() => {
    const list = element?.content?.words;
    return Array.isArray(list) ? list.filter((w) => typeof w === 'string' && w.trim().length > 0).map((w) => w.trim()) : [];
  }, [element?.content?.words]);

  // Editor canvas: just a static tile.
  if (isEditor) {
    return <StaticPreview words={words} />;
  }

  // Preview mode: local state, plays on one screen.
  if (previewMode) {
    return <AliasPreviewShell words={words} />;
  }

  // Live mode: controlled by parent (VirtualClassroom feeds gameState + change handler).
  const state = gameState || createInitialGameState();
  const handleChange = (next) => {
    if (onGameStateChange) onGameStateChange(next);
  };
  return (
    <AliasGameCore
      words={words}
      state={state}
      onChange={handleChange}
      userRole={userRole}
      previewMode={false}
    />
  );
};

const AliasPreviewShell = ({ words }) => {
  const [state, setState] = useState(() => createInitialGameState());
  return (
    <AliasGameCore
      words={words}
      state={state}
      onChange={setState}
      userRole={null}
      previewMode
    />
  );
};

export default AliasElement;
